import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import type { Sketch } from "@shared/types";
import { get } from "@/lib/api";
import { SketchFrame } from "./SketchFrame";

/**
 * Fetches and renders the site background sketch.
 *
 * Re-fetches on every route change, so "random" mode in the admin settings
 * gives a different sketch as you move around the site. Respects
 * prefers-reduced-motion by rendering nothing — the sketches all animate
 * continuously.
 */
export function SketchBackground() {
  const [sketch, setSketch] = useState<Sketch | null>(null);
  const location = useLocation();

  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (reducedMotion) return;
    let cancelled = false;
    void get<{ sketch: Sketch | null }>("/sketches/background")
      .then((res) => {
        if (!cancelled) setSketch(res.sketch);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // Keyed on pathname: a new page load means a new draw from the pool.
  }, [location.pathname, reducedMotion]);

  if (reducedMotion || !sketch) return null;

  return (
    <div className="sketch-bg">
      <SketchFrame key={sketch.id} code={sketch.code} title={sketch.title} variant="background" />
      <div className="sketch-bg__scrim" />
    </div>
  );
}
